import React, { useMemo, useState } from 'react';
import type { PlanoTrabalho } from '../../types/planoTrabalho.types';
import { etapaPlanoInfo } from '../../utils/planoEtapas';
import Button from '../ui/Button';
import CollapseToggle from './CollapseToggle';

const STATUS_META: Record<string, { label: string; cls: string }> = {
  RASCUNHO:              { label: 'Rascunho',       cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' },
  AGUARDANDO_ASSINATURA: { label: 'Aguard. assin.', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200' },
  EM_ANALISE:            { label: 'Em análise',     cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200' },
  APROVADO:              { label: 'Aprovado',       cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200' },
  DEVOLVIDO_CORRECAO:    { label: 'Devolvido',      cls: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200' },
  REPROVADO:             { label: 'Reprovado',      cls: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200' },
  CONCLUIDO:             { label: 'Concluído',      cls: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-100' },
  FECHADO:               { label: 'Fechado',        cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  PENDENTE_RETIFICACAO:  { label: 'Retificação',    cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200' },
};

const statusMeta = (status?: string) =>
  STATUS_META[status ?? ''] ?? { label: status ?? '—', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' };

export interface PlanosTrabalhoBoxProps {
  planos: PlanoTrabalho[];
  onOpenPlano: (id: string) => void;
  onVerTodos: () => void;
  /** Se informado, exibe o botão "Novo Plano". */
  onNovoPlano?: () => void;
  emptyActionLabel: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
  maxVisible?: number;
}

/**
 * Box de Planos de Trabalho da área de trabalho, reutilizável entre perfis.
 * Além da lista, agrupa por status com chips-contadores que filtram localmente.
 */
const PlanosTrabalhoBox: React.FC<PlanosTrabalhoBoxProps> = ({
  planos,
  onOpenPlano,
  onVerTodos,
  onNovoPlano,
  emptyActionLabel,
  collapsed,
  onToggleCollapse,
  maxVisible = 5,
}) => {
  const [filtro, setFiltro] = useState<string | null>(null);

  const contagem = useMemo(() => {
    const map = new Map<string, number>();
    planos.forEach(p => {
      const key = p.status ?? '—';
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [planos]);

  const visiveis = useMemo(
    () => (filtro ? planos.filter(p => (p.status ?? '—') === filtro) : planos),
    [planos, filtro],
  );

  return (
    <div className="flex h-fit min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 shadow-sm dark:border-slate-700">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 bg-slate-50 px-5 py-3 dark:bg-slate-900 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Planos de Trabalho</span>
          {planos.length > 0 && (
            <span className="shrink-0 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium dark:bg-indigo-900/50 dark:text-indigo-300">{planos.length}</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onNovoPlano && (
            <Button variant="primary" size="sm" onClick={onNovoPlano}>
              Novo Plano
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={onVerTodos}>Ver todos</Button>
          <CollapseToggle collapsed={collapsed} onToggle={onToggleCollapse} />
        </div>
      </div>

      {!collapsed && (planos.length === 0 ? (
        <div className="min-h-0 flex-1 overflow-y-auto flex flex-col items-center justify-center bg-white px-5 py-10 text-center dark:bg-slate-950">
          <p className="text-sm font-medium text-fg-muted">Nenhum plano de trabalho cadastrado.</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={onVerTodos}>
            {emptyActionLabel}
          </Button>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto bg-white dark:bg-slate-950">
          {contagem.length > 1 && (
            <div className="flex flex-wrap gap-1.5 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
              <button type="button" onClick={() => setFiltro(null)}
                className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${filtro === null ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'}`}>
                Todos {planos.length}
              </button>
              {contagem.map(([status, qtd]) => {
                const { label } = statusMeta(status);
                const ativo = filtro === status;
                return (
                  <button key={status} type="button" onClick={() => setFiltro(ativo ? null : status)}
                    className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${ativo ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'}`}>
                    {label} {qtd}
                  </button>
                );
              })}
            </div>
          )}

          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {visiveis.slice(0, maxVisible).map(p => {
              const { label: sl, cls: sc } = statusMeta(p.status);
              const etapa = p.status === 'EM_ANALISE' ? etapaPlanoInfo(p.etapaAtual) : null;
              const mostrarProgresso = etapa != null && etapa.passo != null;
              return (
                <div key={p.id}
                  className="cursor-pointer px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-900"
                  onClick={() => p.id && onOpenPlano(p.id)}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate dark:text-slate-100">{p.titulo}</p>
                      {p.emendaCodigo && <p className="text-xs text-fg-subtle mt-0.5 ">Emenda: {p.emendaCodigo}</p>}
                    </div>
                    <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${sc}`}>{sl}</span>
                  </div>
                  {mostrarProgresso && (
                    <div className="mt-2">
                      <div className="flex gap-1" aria-hidden="true">
                        {Array.from({ length: etapa!.total }).map((_, i) => (
                          <div key={i}
                            className={`h-1 flex-1 rounded-full ${
                              i < etapa!.index
                                ? 'bg-emerald-400 dark:bg-emerald-500'
                                : i === etapa!.index
                                  ? 'bg-blue-500'
                                  : 'bg-slate-200 dark:bg-slate-700'
                            }`} />
                        ))}
                      </div>
                      <p className="mt-1 text-[11px] text-fg-subtle ">
                        Etapa {etapa!.passo} de {etapa!.total} · {etapa!.label}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
            {visiveis.length > maxVisible && (
              <div className="px-4 py-2.5 text-center bg-white dark:bg-slate-950">
                <button type="button" onClick={onVerTodos}
                  className="text-xs font-medium text-indigo-600 border border-indigo-200 px-3 py-1 rounded-lg hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-400 dark:hover:bg-indigo-950/30 transition-colors">
                  {visiveis.length - maxVisible} a mais. Ver todos
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PlanosTrabalhoBox;
